import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import {
  ExternalNavigationRequest,
  ExternalNavigationRejectionReason,
  ExternalNavigationUrlParser,
  decideExternalNavigation,
} from '@automatick/shared-ts';

export const BrowserNavigationFailureReason = {
  BrowserUnavailable: 'browser-unavailable',
  PopupBlocked: 'popup-blocked',
  PopupClosed: 'popup-closed',
  NavigationFailed: 'navigation-failed',
  InvalidInternalRoute: 'invalid-internal-route',
} as const;

export type BrowserNavigationFailureReason =
  (typeof BrowserNavigationFailureReason)[keyof typeof BrowserNavigationFailureReason];

export interface ExternalNavigationPopupReservation {
  readonly reservationId: number;
}

export type BrowserNavigationResult =
  | { readonly kind: 'opened'; readonly targetUrl: string }
  | {
      readonly kind: 'policy-rejected';
      readonly reason: ExternalNavigationRejectionReason;
    }
  | {
      readonly kind: 'browser-rejected';
      readonly reason: BrowserNavigationFailureReason;
    };

export type BrowserPopupCompletionResult =
  | { readonly kind: 'opened' }
  | {
      readonly kind: 'browser-rejected';
      readonly reason: BrowserNavigationFailureReason;
    };

export type BrowserDownloadResult =
  | { readonly kind: 'downloaded' }
  | {
      readonly kind: 'browser-rejected';
      readonly reason: BrowserNavigationFailureReason;
    };

type ReservedPopupNavigationResult = Exclude<
  BrowserNavigationResult,
  { readonly kind: 'policy-rejected' }
>;

type NavigationWindow = Window & {
  open(url?: string | URL, target?: string, features?: string): Window | null;
};

type ReservedPopupState =
  | { readonly kind: 'popup'; readonly popup: Window }
  | { readonly kind: 'blocked' }
  | { readonly kind: 'navigation-failed' };

interface BrowserObjectUrlApi {
  createObjectURL(blob: Blob): string;
  revokeObjectURL(url: string): void;
}

const SuccessfulObjectUrlReleaseDelayMs = 60_000;

@Injectable({ providedIn: 'root' })
export class ExternalNavigationService implements OnDestroy {
  private readonly documentRef = inject(DOCUMENT, { optional: true });
  private readonly platformId = inject(PLATFORM_ID);
  private readonly reservedPopups = new Map<number, ReservedPopupState>();
  private readonly outstandingObjectUrls = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();
  private nextReservationId = 1;
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly urlParser: ExternalNavigationUrlParser = {
    parseAbsolute: (targetUrl) => {
      try {
        const parsed = new URL(targetUrl);
        return {
          serializedUrl: parsed.href,
          protocol: parsed.protocol,
          hostname: parsed.hostname,
          username: parsed.username,
          password: parsed.password,
        };
      } catch {
        return null;
      }
    },
  };

  reservePopup(): ExternalNavigationPopupReservation {
    const reservation = Object.freeze({
      reservationId: this.nextReservationId++,
    });
    let popupState: ReservedPopupState = { kind: 'blocked' };
    const browserWindow = this.browserWindow;
    if (browserWindow) {
      try {
        // Chromium returns null for successful noopener/noreferrer opens, so a
        // retained async-safe proxy requires a plain blank popup. We null the
        // opener immediately; this flow does not claim to guarantee noreferrer.
        const popup = browserWindow.open('about:blank', '_blank');
        if (popup) {
          try {
            popup.opener = null;
            popupState = { kind: 'popup', popup };
          } catch {
            this.closePopup(popup);
            popupState = { kind: 'navigation-failed' };
          }
        }
      } catch {
        popupState = { kind: 'navigation-failed' };
      }
    }
    this.reservedPopups.set(reservation.reservationId, popupState);
    return reservation;
  }

  completePopup(
    reservation: ExternalNavigationPopupReservation,
    request: ExternalNavigationRequest,
  ): BrowserNavigationResult {
    const popupState = this.takeReservedPopup(reservation);
    const decision = decideExternalNavigation(request, this.urlParser);
    if (decision.kind === 'rejected') {
      this.closeReservedPopup(popupState);
      return { kind: 'policy-rejected', reason: decision.reason };
    }
    if (!this.browserWindow) {
      this.closeReservedPopup(popupState);
      return {
        kind: 'browser-rejected',
        reason: BrowserNavigationFailureReason.BrowserUnavailable,
      };
    }
    return this.navigateReservedPopup(popupState, decision.targetUrl);
  }

  completePopupWithBlob(
    reservation: ExternalNavigationPopupReservation,
    blob: Blob,
  ): BrowserPopupCompletionResult {
    const popupState = this.takeReservedPopup(reservation);
    if (!popupState) {
      return {
        kind: 'browser-rejected',
        reason: BrowserNavigationFailureReason.NavigationFailed,
      };
    }
    if (!this.browserWindow) {
      this.closeReservedPopup(popupState);
      return {
        kind: 'browser-rejected',
        reason: BrowserNavigationFailureReason.BrowserUnavailable,
      };
    }

    const objectUrl = this.createObjectUrl(blob);
    if (!objectUrl) {
      this.closeReservedPopup(popupState);
      return {
        kind: 'browser-rejected',
        reason: BrowserNavigationFailureReason.NavigationFailed,
      };
    }

    const result = this.navigateReservedPopup(popupState, objectUrl);
    if (result.kind !== 'opened') {
      this.releaseObjectUrl(objectUrl);
      return result;
    }

    this.scheduleObjectUrlRelease(objectUrl);
    return { kind: 'opened' };
  }

  downloadBlob(blob: Blob, fileName: string): BrowserDownloadResult {
    if (!this.browserWindow || !this.documentRef) {
      return {
        kind: 'browser-rejected',
        reason: BrowserNavigationFailureReason.BrowserUnavailable,
      };
    }

    const objectUrl = this.createObjectUrl(blob);
    if (!objectUrl) {
      return {
        kind: 'browser-rejected',
        reason: BrowserNavigationFailureReason.NavigationFailed,
      };
    }

    try {
      const anchor = this.documentRef.createElement('a');
      anchor.href = objectUrl;
      anchor.download = fileName;
      anchor.click();
    } catch {
      this.releaseObjectUrl(objectUrl);
      return {
        kind: 'browser-rejected',
        reason: BrowserNavigationFailureReason.NavigationFailed,
      };
    }

    this.scheduleObjectUrlRelease(objectUrl);
    return { kind: 'downloaded' };
  }

  completeInternalRoute(
    reservation: ExternalNavigationPopupReservation,
    routeUrl: string,
  ): BrowserNavigationResult {
    const popupState = this.takeReservedPopup(reservation);
    const resolvedRoute = this.resolveInternalRoute(routeUrl);
    if (!resolvedRoute) {
      this.closeReservedPopup(popupState);
      return {
        kind: 'browser-rejected',
        reason: this.browserWindow
          ? BrowserNavigationFailureReason.InvalidInternalRoute
          : BrowserNavigationFailureReason.BrowserUnavailable,
      };
    }

    return this.navigateReservedPopup(popupState, resolvedRoute);
  }

  cancelPopup(reservation: ExternalNavigationPopupReservation): void {
    this.closeReservedPopup(this.takeReservedPopup(reservation));
  }

  openExternal(request: ExternalNavigationRequest): BrowserNavigationResult {
    const decision = decideExternalNavigation(request, this.urlParser);
    if (decision.kind === 'rejected') {
      return { kind: 'policy-rejected', reason: decision.reason };
    }

    const browserWindow = this.browserWindow;
    if (!browserWindow) {
      return {
        kind: 'browser-rejected',
        reason: BrowserNavigationFailureReason.BrowserUnavailable,
      };
    }

    const reservation = this.reservePopup();
    const popupState = this.takeReservedPopup(reservation);
    return this.navigateReservedPopup(popupState, decision.targetUrl);
  }

  openTrustedInternalRoute(routeUrl: string): BrowserNavigationResult {
    if (!this.browserWindow) {
      return {
        kind: 'browser-rejected',
        reason: BrowserNavigationFailureReason.BrowserUnavailable,
      };
    }

    const resolvedRoute = this.resolveInternalRoute(routeUrl);
    if (!resolvedRoute) {
      return {
        kind: 'browser-rejected',
        reason: BrowserNavigationFailureReason.InvalidInternalRoute,
      };
    }

    const reservation = this.reservePopup();
    const popupState = this.takeReservedPopup(reservation);
    return this.navigateReservedPopup(popupState, resolvedRoute);
  }

  ngOnDestroy(): void {
    for (const popupState of this.reservedPopups.values()) {
      this.closeReservedPopup(popupState);
    }
    this.reservedPopups.clear();

    for (const objectUrl of [...this.outstandingObjectUrls.keys()]) {
      this.releaseObjectUrl(objectUrl);
    }
  }

  private navigateReservedPopup(
    popupState: ReservedPopupState | null,
    targetUrl: string,
  ): ReservedPopupNavigationResult {
    if (!popupState || popupState.kind === 'navigation-failed') {
      return {
        kind: 'browser-rejected',
        reason: BrowserNavigationFailureReason.NavigationFailed,
      };
    }
    if (popupState.kind === 'blocked') {
      return {
        kind: 'browser-rejected',
        reason: BrowserNavigationFailureReason.PopupBlocked,
      };
    }

    const popup = popupState.popup;
    const closedBeforeNavigation = this.readPopupClosed(popup);
    if (closedBeforeNavigation === null) {
      this.closePopup(popup);
      return {
        kind: 'browser-rejected',
        reason: BrowserNavigationFailureReason.NavigationFailed,
      };
    }
    if (closedBeforeNavigation) {
      return {
        kind: 'browser-rejected',
        reason: BrowserNavigationFailureReason.PopupClosed,
      };
    }

    try {
      popup.location.replace(targetUrl);
    } catch {
      const closedAfterFailure = this.readPopupClosed(popup);
      this.closePopup(popup);
      return {
        kind: 'browser-rejected',
        reason: closedAfterFailure
          ? BrowserNavigationFailureReason.PopupClosed
          : BrowserNavigationFailureReason.NavigationFailed,
      };
    }
    return { kind: 'opened', targetUrl };
  }

  private takeReservedPopup(
    reservation: ExternalNavigationPopupReservation,
  ): ReservedPopupState | null {
    const popupState =
      this.reservedPopups.get(reservation.reservationId) ?? null;
    this.reservedPopups.delete(reservation.reservationId);
    return popupState;
  }

  private closeReservedPopup(popupState: ReservedPopupState | null): void {
    if (popupState?.kind === 'popup') {
      this.closePopup(popupState.popup);
    }
  }

  private closePopup(popup: Window | null): void {
    if (!popup) {
      return;
    }
    try {
      if (!popup.closed) {
        popup.close();
      }
    } catch {
      try {
        popup.close();
      } catch {
        // The browser revoked the popup proxy; no further release is possible.
      }
    }
  }

  private readPopupClosed(popup: Window): boolean | null {
    try {
      return popup.closed;
    } catch {
      return null;
    }
  }

  private resolveInternalRoute(routeUrl: string): string | null {
    const browserWindow = this.browserWindow;
    if (!browserWindow) {
      return null;
    }

    let parsedRoute: URL;
    try {
      parsedRoute = new URL(routeUrl, browserWindow.location.href);
    } catch {
      return null;
    }
    if (parsedRoute.origin !== browserWindow.location.origin) {
      return null;
    }

    return `${parsedRoute.pathname}${parsedRoute.search}${parsedRoute.hash}`;
  }

  private createObjectUrl(blob: Blob): string | null {
    const objectUrlApi = this.objectUrlApi;
    if (!objectUrlApi) {
      return null;
    }
    try {
      const objectUrl = objectUrlApi.createObjectURL(blob);
      return objectUrl.trim() ? objectUrl : null;
    } catch {
      return null;
    }
  }

  private scheduleObjectUrlRelease(objectUrl: string): void {
    try {
      const timer = globalThis.setTimeout(
        () => this.releaseObjectUrl(objectUrl),
        SuccessfulObjectUrlReleaseDelayMs,
      );
      this.outstandingObjectUrls.set(objectUrl, timer);
    } catch {
      this.releaseObjectUrl(objectUrl);
    }
  }

  private releaseObjectUrl(objectUrl: string): void {
    const timer = this.outstandingObjectUrls.get(objectUrl);
    if (timer !== undefined) {
      globalThis.clearTimeout(timer);
      this.outstandingObjectUrls.delete(objectUrl);
    }
    try {
      this.objectUrlApi?.revokeObjectURL(objectUrl);
    } catch {
      // Object URL cleanup is best effort after browser teardown.
    }
  }

  private get objectUrlApi(): BrowserObjectUrlApi | null {
    if (!this.browserWindow) {
      return null;
    }
    const objectUrlApi = globalThis.URL;
    return typeof objectUrlApi.createObjectURL === 'function' &&
      typeof objectUrlApi.revokeObjectURL === 'function'
      ? objectUrlApi
      : null;
  }

  private get browserWindow(): NavigationWindow | null {
    if (!this.isBrowser) {
      return null;
    }
    const defaultView = this.documentRef?.defaultView;
    return defaultView ? (defaultView as NavigationWindow) : null;
  }
}
